import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from "react"
import { CollectionMetadata, CountryIntelligence } from "@oysterhr/schema"
import { EmploymentTerms } from "./constants"
import { EmploymentComparatorContext, EmploymentComparatorState } from "./types"
import { EmploymentComparatorFormValues } from "./EmploymentComparatorForm/types"
import { useCountryIntelligence } from "../../hooks/useCountryIntelligence"

const EmploymentComparatorContext = createContext<EmploymentComparatorContext | null>(null)

export const useEmploymentComparatorContext: () => EmploymentComparatorContext = () => {
  const context = useContext(EmploymentComparatorContext)

  if (!context)
    throw new Error(
      "EmploymentComparatorContext must be called within EmploymentComparatorProvider",
    )

  return context
}

const INITIAL_STATE: EmploymentComparatorState = "empty"

export const EmploymentComparatorProvider = ({ children }: { children: ReactNode }) => {
  const [fetchCountryIntelligence] = useCountryIntelligence({
    onError: error => {
      if (error) {
        setEmploymentComparatorState("error")
      }
    },
    onCompleted: data => {
      setCountryIntelligenceCollection(data?.countryIntelligences.collection)
      if (data?.countryIntelligences.metadata) {
        setPaginationMetadata(data?.countryIntelligences.metadata)
      }
      setEmploymentComparatorState("success")
    },
  })

  const [employmentComparatorState, setEmploymentComparatorState] =
    useState<EmploymentComparatorState>(INITIAL_STATE)
  const [countryIntelligenceCollection, setCountryIntelligenceCollection] = useState<
    CountryIntelligence[] | undefined
  >([])
  const [employmentTerms, setEmploymentTerms] = useState<EmploymentTerms[]>([])
  const [subdivisions, setSubdivisions] = useState<string[]>([])
  const [paginationMetadata, setPaginationMetadata] = useState<CollectionMetadata>()
  const [formValues, setFormValues] = useState<EmploymentComparatorFormValues>()

  const handleFetchWithValues = useCallback(
    async (formValues: EmploymentComparatorFormValues) => {
      fetchCountryIntelligence({
        variables: {
          countryCodes: formValues.countries.filter(country => country !== "ALL"),
          expandSubdivisions: formValues.subdivisions.length > 0,
          probationPeriod: !!formValues.employmentTerms.includes(EmploymentTerms.ProbationPeriod),
          sickDays: !!formValues.employmentTerms.includes(EmploymentTerms.SickDays),
          parentalLeave: !!formValues.employmentTerms.includes(EmploymentTerms.ParentalLeave),
          terminationNotice: !!formValues.employmentTerms.includes(
            EmploymentTerms.TerminationNotice,
          ),
          benefits: !!formValues.employmentTerms.includes(EmploymentTerms.Benefits),
          allowance: !!formValues.employmentTerms.includes(EmploymentTerms.Allowance),
          vacationDays: !!formValues.employmentTerms.includes(EmploymentTerms.VacationDays),
          perPage: 10,
          page: 1,
        },
      })
      setEmploymentTerms(
        formValues.employmentTerms.filter(term => term !== "ALL") as EmploymentTerms[],
      )
    },
    [fetchCountryIntelligence],
  )

  useEffect(() => {
    if (formValues) {
      handleFetchWithValues(formValues)
    }
  }, [formValues, handleFetchWithValues])

  return (
    <EmploymentComparatorContext.Provider
      value={{
        employmentComparatorState,
        setEmploymentComparatorState,
        countryIntelligenceCollection,
        setCountryIntelligenceCollection,
        employmentTerms,
        setEmploymentTerms,
        subdivisions,
        setSubdivisions,
        paginationMetadata,
        setPaginationMetadata,
        fetchCountryIntelligence,
        setFormValues,
        formValues,
      }}
    >
      {children}
    </EmploymentComparatorContext.Provider>
  )
}
